import React, { useState } from "react";
import { Sparkles, Loader2, Download, ArrowRight, Upload, X, Globe } from "lucide-react";
import { advertStudioGenerate, adminAdvertStudioGenerate, uploadAdvertImage } from "@/lib/api";
import { Link } from "react-router-dom";

/**
 * "HorseAppCompare Advert Studio" — generates a single preview ad creative.
 *
 * - Public mode (default): used on /sponsor. Rate-limited 3/day per IP by the backend.
 * - Admin mode (admin={true}): hits the admin endpoint, no daily limit.
 *
 * After preview, the CTA pushes to /advert-submit for paid placement (public mode).
 */
const AdvertStudioPublic = ({ isNl = true, admin = false }) => {
  const [form, setForm] = useState({
    website_url: "",
    reference_images: [],
    app_name: "",
    tagline: "",
    audience: "horse owners, riders, trainers",
    brand_color: "#2563EB",
    format: "banner",
    extra: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(null);

  const onUploadRef = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if ((form.reference_images || []).length >= 3) {
      setError(isNl ? "Maximaal 3 referentie-afbeeldingen." : "Maximum 3 reference images.");
      return;
    }
    setUploading(true); setError("");
    try {
      const data = await uploadAdvertImage(file);
      setForm((s) => ({ ...s, reference_images: [...(s.reference_images || []), data.url] }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeRef = (idx) => setForm((s) => ({ ...s, reference_images: s.reference_images.filter((_, i) => i !== idx) }));

  const run = async (e) => {
    e.preventDefault();
    setError(""); setResult(null);
    if (!form.app_name || !form.tagline) {
      setError(isNl ? "App-naam en tagline zijn verplicht." : "App name and tagline are required.");
      return;
    }
    setBusy(true);
    try {
      const data = await (admin ? adminAdvertStudioGenerate(form) : advertStudioGenerate(form));
      setResult(data);
      if (!admin) setRemaining(data.remaining_today);
    } catch (err) {
      setError(err?.response?.data?.detail || (isNl ? "Genereren mislukt, probeer opnieuw." : "Generation failed, please retry."));
      if (err?.response?.status === 429) setRemaining(0);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-gradient-to-br from-violet-50 via-white to-indigo-50 border-y border-violet-200" data-testid="advert-studio-section">
      <div className="hac-container py-16 max-w-5xl">
        <div className="grid md:grid-cols-[1.1fr_1fr] gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 bg-violet-600 text-white px-3 py-1 rounded-full text-xs font-bold mb-4">
              <Sparkles size={12} /> HorseAppCompare Advert Studio
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] leading-tight mb-3">
              {admin
                ? (isNl ? "Advert Studio — Admin (onbeperkt)" : "Advert Studio — Admin (unlimited)")
                : (isNl ? "Maak je advertentie in 30 seconden — gratis preview" : "Create your ad in 30 seconds — free preview")}
            </h2>
            <p className="text-[#64748B] leading-relaxed mb-6">
              {isNl
                ? "Geen designer? Geen probleem. Onze AI-art-director genereert een passende advertentie in jouw huisstijl — premium kwaliteit, magazine-editorial look, geen stock-photo cliché."
                : "No designer? No problem. Our AI art director generates an on-brand creative in seconds — premium quality, editorial look, no stock-photo cliché."}
            </p>
            <ul className="text-sm text-[#0F172A] space-y-1.5 mb-6">
              {admin ? (
                <>
                  <li>✓ {isNl ? "Onbeperkt aantal previews (admin-modus)" : "Unlimited previews (admin mode)"}</li>
                  <li>✓ {isNl ? "Banner of vierkant formaat" : "Banner or square format"}</li>
                  <li>✓ {isNl ? "Volledige website-scrape + 3 referentie-afbeeldingen" : "Full website scrape + 3 reference images"}</li>
                  <li>✓ {isNl ? "Download direct als PNG en upload via Ads" : "Download as PNG and create an Ad from it"}</li>
                </>
              ) : (
                <>
                  <li>✓ {isNl ? "1 gratis preview per dag (3× per IP)" : "1 free preview per day (3× per IP)"}</li>
                  <li>✓ {isNl ? "Banner of vierkant formaat" : "Banner or square format"}</li>
                  <li>✓ {isNl ? "Premium look, geen generieke AI-stijl" : "Premium aesthetic, no generic AI look"}</li>
                  <li>✓ {isNl ? "Wil je hem plaatsen? → Submit naar /sponsor" : "Want to run it? → Submit on /sponsor"}</li>
                </>
              )}
            </ul>
            {!admin && remaining !== null && (
              <p className="text-xs text-[#64748B]">
                {remaining > 0
                  ? (isNl ? `${remaining} gratis preview${remaining === 1 ? "" : "s"} over vandaag.` : `${remaining} free preview${remaining === 1 ? "" : "s"} left today.`)
                  : (isNl ? "Dagelijkse limiet bereikt. Submit je advertentie om door te gaan." : "Daily limit reached. Submit your ad to continue.")}
              </p>
            )}
          </div>

          <form onSubmit={run} className="bg-white border border-violet-200 rounded-2xl p-5 space-y-3 shadow-sm" data-testid="advert-studio-form">
            <div>
              <label className="overline block mb-1 inline-flex items-center gap-1"><Globe size={11} /> {isNl ? "Website van je app" : "Your app's website"}</label>
              <input className="hac-input" value={form.website_url} maxLength={200} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://yourapp.com" data-testid="advert-website" />
              <div className="text-[10px] text-[#94A3B8] mt-1">{isNl ? "We lezen je website-info om de advertentie passend te maken." : "We read your website meta to ground the ad in your brand."}</div>
            </div>

            <div>
              <label className="overline block mb-1">{isNl ? "Referentie-afbeeldingen (max 3)" : "Reference images (max 3)"}</label>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {(form.reference_images || []).map((url, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-violet-200 bg-white" data-testid={`advert-ref-${i}`}>
                    <img src={url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt={`ref ${i + 1}`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeRef(i)} className="absolute top-1 right-1 bg-white/90 hover:bg-red-500 hover:text-white text-[#0F172A] rounded-full p-0.5" data-testid={`advert-ref-remove-${i}`}><X size={11} /></button>
                  </div>
                ))}
                {(form.reference_images || []).length < 3 && (
                  <label className="aspect-square rounded-lg border-2 border-dashed border-violet-300 hover:border-violet-500 bg-violet-50/50 hover:bg-violet-50 flex flex-col items-center justify-center cursor-pointer text-violet-600 text-[10px] text-center px-1" data-testid="advert-ref-upload">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <><Upload size={14} className="mb-0.5" /> {isNl ? "Upload" : "Upload"}</>}
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onUploadRef} disabled={uploading} />
                  </label>
                )}
              </div>
              <div className="text-[10px] text-[#94A3B8]">{isNl ? "Bv. screenshots van je app, logo's, sfeer-foto's." : "E.g. app screenshots, logos, mood photos."}</div>
            </div>

            <div className="border-t border-violet-100 my-2"></div>

            <div className="grid grid-cols-1 gap-2">
              <div>
                <label className="overline block mb-1">{isNl ? "App-naam" : "App name"} *</label>
                <input className="hac-input" value={form.app_name} maxLength={60} onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder={isNl ? "bv. RideMaster" : "e.g. RideMaster"} required data-testid="advert-app-name" />
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "Tagline" : "Tagline"} *</label>
                <input className="hac-input" value={form.tagline} maxLength={160} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder={isNl ? "Wat doet jouw app in 1 zin?" : "What does your app do in 1 sentence?"} required data-testid="advert-tagline" />
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "Doelgroep" : "Audience"}</label>
                <input className="hac-input" value={form.audience} maxLength={120} onChange={(e) => setForm({ ...form, audience: e.target.value })} placeholder={isNl ? "bv. dressuurruiters, manegehouders" : "e.g. dressage riders, barn owners"} data-testid="advert-audience" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="overline block mb-1">{isNl ? "Huisstijl-kleur" : "Brand color"}</label>
                  <input type="color" className="hac-input p-1 h-9" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} data-testid="advert-color" />
                </div>
                <div>
                  <label className="overline block mb-1">{isNl ? "Formaat" : "Format"}</label>
                  <select className="hac-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} data-testid="advert-format">
                    <option value="banner">{isNl ? "Banner 3:1" : "Banner 3:1"}</option>
                    <option value="square">{isNl ? "Vierkant 1:1" : "Square 1:1"}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="overline block mb-1">{isNl ? "Extra wens (optioneel)" : "Extra direction (optional)"}</label>
                <input className="hac-input" value={form.extra} maxLength={200} onChange={(e) => setForm({ ...form, extra: e.target.value })} placeholder={isNl ? "bv. dressuurfocus, warm avondlicht" : "e.g. dressage focus, warm evening light"} data-testid="advert-extra" />
              </div>
            </div>
            <button type="submit" disabled={busy} className="w-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-full inline-flex items-center justify-center gap-2" data-testid="advert-generate-btn">
              {busy ? <><Loader2 size={14} className="animate-spin" /> {isNl ? "Genereren (~30s)…" : "Generating (~30s)…"}</> : <><Sparkles size={14} /> {isNl ? "Genereer gratis preview" : "Generate free preview"}</>}
            </button>
            {error && <div className="text-sm text-red-600" data-testid="advert-error">{error}</div>}
          </form>
        </div>

        {result && (
          <div className="mt-10 bg-white border border-violet-200 rounded-2xl p-6 shadow-md" data-testid="advert-result">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-heading text-xl text-[#0F172A]">
                {isNl ? "Jouw preview is klaar 🎨" : "Your preview is ready 🎨"}
              </h3>
              <div className="flex gap-2">
                <a href={result.url} target="_blank" rel="noopener noreferrer" download className="text-xs bg-white border border-[#E2E8F0] hover:border-[#0F172A] px-3 py-1.5 rounded-full inline-flex items-center gap-1" data-testid="advert-download">
                  <Download size={11} /> {isNl ? "Download" : "Download"}
                </a>
                {!admin && (
                  <Link to={`/advert-submit?image=${encodeURIComponent(result.url)}`} className="text-xs bg-[#0F172A] hover:bg-[#1D293A] text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1" data-testid="advert-submit-cta">
                    {isNl ? "Plaats deze advertentie" : "Submit this ad"} <ArrowRight size={11} />
                  </Link>
                )}
              </div>
            </div>
            <img src={result.url} alt="Generated advert preview" className="w-full rounded-xl border border-[#E2E8F0]" data-testid="advert-image" />
            <p className="text-[11px] text-[#94A3B8] mt-2 italic">
              {isNl
                ? "Deze preview is van jou — je mag hem downloaden en gebruiken. Plaats hem op HorseAppCompare via /sponsor om bezoekers te bereiken."
                : "This preview is yours — feel free to download and use. Place it on HorseAppCompare via /sponsor to reach visitors."}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdvertStudioPublic;
